<template>
  <!-- 测试信息：按任务/时间过滤 + 消息流 + 发布(图片上传 + 文本域含粘贴上传) + 消息往来drawer -->
  <div class="test-info">
    <!-- 顶部：按任务 + 时间范围过滤（吸顶不随列表滚动） -->
    <div class="msg-filter">
      <span class="filter-label">按任务过滤</span>
      <el-select v-model="filterTaskId" placeholder="全部任务" clearable filterable style="width: 260px">
        <el-option v-for="t in taskList" :key="t.id" :label="(t.taskCode || '') + ' ' + (t.taskName || '')" :value="t.id"/>
      </el-select>
      <span class="filter-label">时间范围</span>
      <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="YYYY-MM-DD"
          style="width: 260px"/>
      <page-button type="read" el-type="primary" @click="resetFilter">重置</page-button>
    </div>
    <div class="message-body">
      <!-- 消息流（线性，去父子结构仅前端展示） -->
      <div v-if="!visibleMessages.length" class="empty-block">暂无消息</div>
      <div v-for="msg in visibleMessages" :key="msg.id" class="msg-item" :id="'msg-' + msg.id"
           :class="{'msg-flash': flashId === msg.id}">
        <div class="msg-avatar">
          <img v-if="avatarUrlOf(msg)" :src="avatarUrlOf(msg)" alt="" class="msg-avatar-img" @error="imgErr($event, msg)"/>
          <span v-else class="msg-avatar-txt">{{ avatarText(msg) }}</span>
        </div>
        <div class="msg-main">
          <div class="msg-head">
            <b class="msg-author">{{ nameOf(msg) }}</b>
            <span class="msg-time">{{ dateTime(msg.createdTime) }}</span>
            <el-tag v-if="msg.taskId && taskOf(msg)" size="small" type="info" class="msg-tasktag">{{ taskOf(msg).taskName }}</el-tag>
            <span v-if="msg.parentId && replyToNameOf(msg)" class="msg-replyto" title="定位源消息" @click="jumpToSource(msg)">回复 @{{ replyToNameOf(msg) }}</span>

          </div>
          <div class="msg-content">
            <div v-if="msg.content" class="msg-text">{{ plainContent(msg.content) }}</div>
            <image-upload
                v-if="dispImages(msg.images)"
                :model-value="dispImages(msg.images)"
                :disabled="true"
                :limit="9"
                class="msg-thumbs"/>
          </div>
          <div class="msg-actions">
            <table-button size="mini" @click="startReply(msg)">回复</table-button>
            <template v-if="!msg.parentId && msg.replyCount > 0">
              <table-button size="mini" @click="viewThread(msg.id)">查看{{ msg.replyCount ? ' ' + msg.replyCount + ' 条' : '' }}回复</table-button>
            </template>
            <table-button v-if="msg.parentId && replyToNameOf(msg)" title="查看全部对话" @click="viewThread(msg.rootId)">查看对话</table-button>
            <table-button size="mini" el-type="danger" message="确认删除该消息？" @click="deleteMsg(msg)">删除</table-button>
          </div>
        </div>
      </div>

      <!-- 加载下一页（无更多时置灰）；老数据保留并追加显示 -->
      <div v-if="visibleMessages.length" class="load-more">
        <page-button
            :disabled="!hasMore"
            @click="loadMore">
          {{ hasMore ? '加载下一页' : '没有更多了' }}
        </page-button>
      </div>

      <!-- 发布消息（放最下方） -->
      <div class="msg-composer">
        <div class="msg-composer-head">
          <div>
            <b v-if="composer.replyToId">回复 @{{ composer.replyToName }}</b>
            <b v-else>发布消息</b>
            <el-button v-if="composer.replyToId" type="text" @click="cancelReply">取消回复</el-button>
          </div>
          <el-select v-model="composer.taskId" placeholder="可关联任务（可不选）" clearable filterable style="width: 280px">
            <template #prefix>关联任务</template>
            <el-option v-for="t in taskList" :key="t.id" :label="(t.taskCode || '') + ' ' + (t.taskName || '')" :value="t.id"/>
          </el-select>
        </div>

        <!-- 文本域 + 图片上传：文本 v-model:text，图片 v-model:images；粘贴上传在组件内部完成 -->
        <yq-textarea
            v-model:text="composer.content"
            v-model:images="composer.images"
            :upload-api="uploadApi"
            folder="projectMessage"
            :limit="9"
            placeholder="请输入消息内容（可 Ctrl+V 粘贴图片）">
          <page-button type="write" el-type="primary" @click="publishMsg">发布</page-button>
        </yq-textarea>
      </div>
    </div>

    <!-- 消息往来：严格父子结构（drawer） -->
    <el-drawer v-model="threadDrawer" title="消息往来" size="620px" :with-header="true">
      <div class="thread-wrap">
        <div v-if="!threadRows.length" class="empty-block">暂无回复</div>
        <template v-else>
          <div v-for="m in threadRows" :key="m.id" class="msg-item" :class="'depth-' + threadDepth(m)">
            <div class="msg-avatar">
              <img v-if="avatarUrlOf(m)" :src="avatarUrlOf(m)" alt="" class="msg-avatar-img" @error="imgErr($event, m)"/>
              <span v-else class="msg-avatar-txt">{{ avatarText(m) }}</span>
            </div>
            <div class="msg-main">
              <div class="msg-head">
                <b class="msg-author">{{ nameOf(m) }}</b>
                <span class="msg-time">{{ dateTime(m.createdTime) }}</span>
                <span v-if="m.parentId && replyToNameOf(m)" class="msg-replyto">回复 @{{ replyToNameOf(m) }}</span>
              </div>
              <div class="msg-content">
                <div v-if="m.content" class="msg-text">{{ plainContent(m.content) }}</div>
                <image-upload
                    v-if="dispImages(m.images)"
                    :model-value="dispImages(m.images)"
                    :disabled="true"
                    :limit="9"
                    class="msg-thumbs"/>
              </div>
            </div>
          </div>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import imageUtil from "@/util/imageUtil";
import ImageUpload from "@/components/h5/image/ImageUpload.vue";
import YqTextarea from "@/components/h5/data/YqTextarea.vue";
import projectApi from "@/api/dz-project/project/projectApi";
import projectMessageApi from "@/api/dz-project/project/projectMessageApi";

export default {
  name: "TestInfo",
  components: {ImageUpload, YqTextarea},
  props: {
    projectId: {type: String, default: ""},
  },
  data() {
    return {
      // 图片上传接口（可换成业务接口）
      uploadApi: "/basedata/common/file/uploadImage",
      taskList: [],
      allMessages: [],       // 已加载的消息（分页累加）
      pageNumber: 1,         // 已加载到第几页
      pageSize: 10,
      totalCount: 0,         // 消息总数（用于判断是否还有下一页）
      loadingMore: false,
      filterTaskId: "",      // 顶部按任务过滤
      filterDateRange: [],   // 顶部按时间范围过滤
      composer: {content: "", replyToId: "", replyToName: "", taskId: "", images: ""},
      threadDrawer: false,
      threadRows: [],
      flashId: "",
    };
  },
  computed: {
    /** 是否还有下一页 */
    hasMore() {
      return (this.allMessages || []).length < (this.totalCount || 0);
    },
    visibleMessages() {
      const list = this.allMessages || [];
      // 1) 任务过滤：命中该任务的消息；回复连同其整个话题上下文一起展示
      let base = list;
      if (this.filterTaskId) {
        const ids = new Set();
        list.forEach(m => {
          if (String(m.taskId) === String(this.filterTaskId)) {
            ids.add(m.id);
            if (m.parentId) {
              const root = list.find(x => x.id === (m.rootId || m.parentId));
              if (root) {
                ids.add(root.id);
                list.forEach(x => x.rootId === root.id && ids.add(x.id));
              }
            }
          }
        });
        base = list.filter(m => ids.has(m.id));
      }
      // 2) 时间范围过滤（按 createdTime 的日期部分）
      if (this.filterDateRange && this.filterDateRange.length === 2) {
        const [start, end] = this.filterDateRange;
        base = base.filter(m => {
          const day = m.createdTime ? String(m.createdTime).slice(0, 10) : "";
          return day >= start && day <= end;
        });
      }
      return base;
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.loadTasks();
        this.loadMessages();
      },
    },
  },
  methods: {
    async loadTasks() {
      if (!this.projectId) {
        return;
      }
      const list = await projectApi.queryProjectTasks(this.projectId);
      this.taskList = list || [];
    },
    /** 重新加载第一页（重置列表） */
    async loadMessages() {
      if (!this.projectId) {
        return;
      }
      this.pageNumber = 1;
      const {records, totalCount} = await projectMessageApi.queryList({
        projectId: this.projectId,
        pageNumber: 1,
        pageSize: this.pageSize,
      });
      this.allMessages = records || [];
      this.totalCount = totalCount || 0;
    },
    /** 加载下一页并追加显示（保留老数据） */
    async loadMore() {
      if (!this.projectId || this.loadingMore || !this.hasMore) {
        return;
      }
      this.loadingMore = true;
      try {
        const nextPage = this.pageNumber + 1;
        const {records, totalCount} = await projectMessageApi.queryList({
          projectId: this.projectId,
          pageNumber: nextPage,
          pageSize: this.pageSize,
        });
        this.allMessages = (this.allMessages || []).concat(records || []);
        this.totalCount = totalCount || 0;
        this.pageNumber = nextPage;
      } finally {
        this.loadingMore = false;
      }
    },
    // ============ 展示工具 ============
    dispImages(images) {
      if (!images) {
        return "";
      }
      try {
        const arr = JSON.parse(images);
        if (Array.isArray(arr)) {
          return arr.map(it => (it && typeof it === "object" ? it.url : it)).join(",");
        }
      } catch (e) {
        // 已是逗号分隔
      }
      return String(images);
    },
    plainContent(content) {
      if (!content) {
        return "";
      }
      if (/<\/?[a-z][\s\S]*>/i.test(content)) {
        const div = document.createElement("div");
        div.innerHTML = content;
        const text = div.textContent || div.innerText || "";
        return text.replace(/&nbsp;/g, " ").replace(/\n{3,}/g, "\n\n").trim();
      }
      return content;
    },
    nameOf(msg) {
      return msg.authorName || msg.createdId || "—";
    },
    avatarUrlOf(msg) {
      if (msg.authorIcon) {
        return imageUtil.getFileUrlSingle(msg.authorIcon);
      }
      return "";
    },
    avatarText(msg) {
      return (this.nameOf(msg) || "?").slice(0, 1);
    },
    imgErr(ev, msg) {
      msg.authorIcon = "";
    },
    taskOf(msg) {
      if (!msg.taskId) {
        return null;
      }
      return this.taskList.find(t => String(t.id) === String(msg.taskId)) || null;
    },
    replyToNameOf(msg) {
      if (!msg.parentId) {
        return "";
      }
      const parent = this.allMessages.find(x => x.id === msg.parentId);
      return parent ? this.nameOf(parent) : "";
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    dateTime(v) {
      return v ? String(v).slice(0, 16) : "—";
    },
    // ============ 发布/回复 ============
    startReply(msg) {
      this.composer.replyToId = msg.id;
      this.composer.replyToName = this.nameOf(msg);
      this.composer.taskId = msg.taskId || "";
      this.composer.images = "";
      // 点击回复后滚动到发布消息区域（等"回复@xx"渲染后再滚）
      this.scrollToComposer();
    },
    cancelReply() {
      this.composer.replyToId = "";
      this.composer.replyToName = "";
      this.composer.taskId = "";
      this.composer.content = "";
      this.composer.images = "";
    },
    async publishMsg() {
      const text = (this.composer.content || "").trim();
      const imagesStr = (this.composer.images || "").trim();
      if (!text && !imagesStr) {
        this.$message.warning("请输入消息内容或添加图片");
        return;
      }
      const payload = {
        projectId: this.projectId,
        taskId: this.composer.taskId || null,
        parentId: this.composer.replyToId || null,
        content: text,
      };
      payload.images = imagesStr ? JSON.stringify(imagesStr.split(",").filter(Boolean)) : null;
      await projectMessageApi.save(payload);
      this.cancelReply();
      await this.loadMessages();
    },
    async deleteMsg(msg) {
      await projectMessageApi.delete(msg.id);
      await this.loadMessages();
    },
    resetFilter() {
      this.filterTaskId = "";
      this.filterDateRange = [];
    },
    async viewThread(id) {
      const rows = await projectMessageApi.queryThread(id);
      this.threadRows = rows || [];
      this.threadDrawer = true;
    },
    jumpToSource(msg) {
      if (!msg.parentId) {
        return;
      }
      // 源消息未在当前已加载列表中 → 提示查看对话
      const loaded = (this.allMessages || []).some(m => m.id === msg.parentId);
      if (!loaded) {
        this.$message.warning("暂未加载这条消息，请查看对话");
        return;
      }
      this.filterTaskId = "";
      this.$nextTick(() => {
        const target = document.getElementById("msg-" + msg.parentId);
        if (target) {
          target.scrollIntoView({behavior: "smooth", block: "center"});
          this.flashId = msg.parentId;
          setTimeout(() => {
            this.flashId = "";
          }, 2000);
        }
      });
    },
    /** 滚动到发布消息区域（发布区在消息流底部） */
    scrollToComposer() {
      this.$nextTick(() => {
        const el = this.$el.querySelector(".msg-composer");
        const wrap = this.$el.querySelector(".message-body");
        if (wrap) {
          // 直接滚到底部，保证吸顶过滤栏下方可见发布区
          wrap.scrollTo({top: wrap.scrollHeight, behavior: "smooth"});
        } else if (el) {
          el.scrollIntoView({behavior: "smooth", block: "end"});
        }
      });
    },
    threadDepth(m) {
      const map = {};
      this.threadRows.forEach(r => {
        map[r.id] = r;
      });
      let depth = 0;
      let cur = m;
      while (cur && cur.parentId && map[cur.parentId]) {
        depth++;
        cur = map[cur.parentId];
        if (depth > 10) {
          break;
        }
      }
      return Math.min(depth, 5);
    },
  },
};
</script>

<style lang="scss" scoped>
.test-info {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .msg-filter {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 8px 12px;
    background: #fff;
    border-bottom: 1px solid var(--el-border-color-lighter);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    .filter-label {
      flex: none;
      font-size: 13px;
      color: var(--yq-font-color-desc);
    }
  }
  .message-body {
    flex: 1;
    overflow: auto;
    padding: 4px 2px;
    .empty-block {
      color: #909399;
      padding: 12px 0;
    }
    .load-more {
      text-align: center;
      padding: 12px 0;
    }
  }
  .msg-composer {
    border: 1px solid var(--el-border-color-lighter);
    border-radius: 6px;
    padding: 10px;
    margin-top: 18px;
    .msg-composer-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 8px;
      color: #409eff;
    }
  }
  .msg-item {
    display: flex;
    gap: 10px;
    padding: 8px 4px;
    border-bottom: 1px solid var(--el-border-color-lighter);
    &.msg-flash {
      background: #fdecec;
      animation: msg-flash 1s ease-in-out infinite;
    }
    .msg-avatar {
      flex: none;
      .msg-avatar-img,
      .msg-avatar-txt {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .msg-avatar-img {
        object-fit: cover;
      }
      .msg-avatar-txt {
        background: #409eff;
        color: #fff;
        font-size: 14px;
      }
    }
    .msg-main {
      flex: 1;
      min-width: 0;
      .msg-head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        .msg-author {
          color: #303133;
        }
        .msg-time {
          font-size: 12px;
          color: #909399;
        }
        .msg-tasktag {
          margin-left: 2px;
        }
        .msg-replyto {
          font-size: 12px;
          color: #409eff;
          cursor: pointer;
          &:hover {
            text-decoration: underline;
          }
        }
      }
      .msg-content {
        margin: 4px 0;
        word-break: break-word;
        .msg-text {
          white-space: pre-wrap;
          line-height: 1.6;
        }
        .msg-thumbs {
          margin-top: 6px;
          display: block;
        }
      }
      .msg-actions {
        display: flex;
        gap: 4px;
      }
    }
  }
}
@keyframes msg-flash {
  0%, 100% {
    background-color: #fdecec;
  }
  50% {
    background-color: #ffb3b3;
  }
}
</style>

<style lang="scss">
/* el-drawer teleport 到 body，需全局样式 */
.thread-wrap {
  padding: 0 8px;
  .msg-item {
    display: flex;
    gap: 8px;
    padding: 8px 4px;
    border-bottom: 1px solid var(--el-border-color-lighter);
    &.depth-0 {
      background: #f6f8fc;
      border-radius: 4px;
      padding: 10px;
      margin-bottom: 4px;
    }
    &.depth-1 {
      margin-left: 28px;
    }
    &.depth-2 {
      margin-left: 56px;
    }
    &.depth-3 {
      margin-left: 84px;
    }
    &.depth-4, &.depth-5 {
      margin-left: 112px;
    }
    .msg-avatar {
      flex: none;
      .msg-avatar-img,
      .msg-avatar-txt {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .msg-avatar-img {
        object-fit: cover;
      }
      .msg-avatar-txt {
        background: #409eff;
        color: #fff;
        font-size: 13px;
      }
    }
    .msg-main {
      flex: 1;
      min-width: 0;
      .msg-head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        .msg-author {
          color: #303133;
        }
        .msg-time {
          font-size: 12px;
          color: #909399;
        }
        .msg-replyto {
          font-size: 12px;
          color: #409eff;
        }
      }
      .msg-content {
        margin: 4px 0;
        word-break: break-word;
        .msg-text {
          white-space: pre-wrap;
          line-height: 1.6;
        }
        .msg-thumbs {
          margin-top: 6px;
          display: block;
        }
      }
    }
  }
  .empty-block {
    color: #909399;
    padding: 12px 0;
  }
}
</style>
